import {forwardRef, useImperativeHandle, useState} from "react";
import styled from "styled-components";
import type {IMassInputV5Props} from "../../../../modules/abstract/sidepanel/tabs/details/field/mass/IMassInput";
import type {IModel} from "../../../../../data/models/Model";
import {TimeUtils} from "../../../../../utils/TimeUtils";
import {FieldDataType} from "../../../../../generated/api/base";
import {FieldDataTypes} from "../../../../../data/models/field/FieldDataTypes";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import {SelectInputV5} from "../../../input/select/SelectInputV5";
import {useAppStore} from "../../../../../StateManager";
import {ConfirmWindowV5} from "../../../popup/ConfirmWindowV5";
import {ClickToEditInputV5} from "../../../input/clicktoedit/ClickToEditInputV5";
import {SingleSelectInputV5} from "../SingleSelectInputV5";
import {baseDistance, VerticalFlexStyle} from "../../../styles/styles";
import {Spinner} from "../../../loader/Spinner";
import {PeopleSingleChoiceInputV5} from "../PeopleSingleChoiceInputV5";

export type IOptionId = "all" | "blank" | string;

type InputType = string | string[] | boolean | number;

interface IOption {
	id: IOptionId;
	label: string;
	value?: any;
}

export const MassFieldInputV5 = forwardRef((props: IMassInputV5Props, ref) => {
	const appState = useAppStore((store) => store.appState);
	const [selectedOptionId, setSelectedOptionId] = useState<IOptionId>("all");
	const [selectedOptionValue, setSelectedOptionValue] = useState<any>(undefined);
	const [inputValue, setInputValue] = useState<InputType>(getInitialInputValueForFieldType());

	const onApply = async (items: IModel[]) => {
		const {field, selectItemsCount} = props;

		// KeyboardListener.getInstance().signals.up.remove(pressButton);

		const itemsToUpdate = items.filter((item) => {
			const hasValue = appState.actions.hasOwnFieldValue(item, field.refId);

			if (!hasValue) {
				return false;
			}

			const value_ = appState.actions.getOwnFieldValue(item, field.refId);
			const value = field.dataType === FieldDataType.Boolean ? value_ : appState.actions.formatValue(value_, field.refId);
			const changeChecker = FieldDataTypes.map[field.dataType].changeChecker;

			if (selectedOptionId === "all") {
				if (changeChecker) {
					return changeChecker(value_, inputValue, selectedOptionValue, field.refId, appState);
				}

				return value !== inputValue;
			} else if (selectedOptionId === "blank") {
				return StringUtils.isBlank(value);
			} else {
				if (changeChecker) {
					return changeChecker(value_, inputValue, selectedOptionValue, field.refId, appState);
				}
				const currentValue = selectedOptionValue;

				return currentValue === value && value !== inputValue;
			}
		});

		const isConfirmed = await ConfirmWindowV5.open(
			`${itemsToUpdate.length} of ${selectItemsCount} items will be updated. Continue?`,
			"Confirm Update",
			{
				ok: "Yes",
				cancel: "Cancel",
			},
		);

		if (isConfirmed) {
			if (itemsToUpdate.length > 0) {
				TimeUtils.waitUpdate(
					appState.actions.updateMassFields(itemsToUpdate, {
						[field.refId]: inputValue,
					}),
					appState.app.notificationContainer,
				);
			}
			props.onClose();
		}
	};

	const onOptionChange = (option: IOption) => {
		setSelectedOptionId(option.id);
		setSelectedOptionValue(option.value);
	};

	const onInputChange = (value: string | boolean) => {
		const finalValue: InputType = value === null ? getInitialInputValueForFieldType() : value;
		// Multiple choice list is handled in MassFieldMultiInput

		setInputValue(finalValue);
	};

	const getEditInput = () => {
		const {items, field} = props;
		const isAnyBeingUpdated = appState.actions.isAnyItemFieldBeingUpdated(items, field);

		switch (field.dataType) {
			case FieldDataType.SingleChoiceList:
				return (
					<>
						<SingleSelectInputV5
							value={inputValue}
							dataTypeSettings={field.dataTypeSettings}
							onChange={onInputChange}
							onFocusLossForceBlur={true}
						/>
						<Spinner
							isVisible={isAnyBeingUpdated}
							isYellow={field?.hasValidation}
						/>
					</>
				);
			case FieldDataType.PeopleSingleChoiceList:
				return (
					<>
						<PeopleSingleChoiceInputV5
							value={appState.actions.getUserById(inputValue as string)}
							dataTypeSettings={field.dataTypeSettings}
							onChange={onInputChange}
							onFocusLossForceBlur={true}
						/>
						<Spinner
							isVisible={isAnyBeingUpdated}
							isYellow={field?.hasValidation}
						/>
					</>
				);
			default:
				return (
					<ClickToEditInputV5
						dataType={field.dataType}
						onLiveChange={onInputChange}
						value={inputValue}
						dataTypeSettings={field.dataTypeSettings}
						onChange={onInputChange}
						isUpdating={isAnyBeingUpdated}
						onFocusLossForceBlur={true}
					/>
				);
		}
	};

	function getInitialInputValueForFieldType(): string | string[] | boolean | number {
		const {field} = props;

		switch (field.dataType) {
			case FieldDataType.Numeric:
			case FieldDataType.DateTime:
				return undefined;
			case FieldDataType.Boolean:
				return false;
			case FieldDataType.PeopleSingleChoiceList:
				return null;
			default:
				return null;
		}
	}

	useImperativeHandle(ref, () => ({
		onApply,
	}));

	const options: IOption[] = [
		{
			id: "all",
			label: "All",
		},
	];

	return (
		<div className="MassFieldInput">
			<div className="container">
				<OptionSelectorInputStyled>
					<SelectInputV5
						options={options}
						renderOptionWhenClosed={(option) => option.label}
						selected={options.find((option) => option.id === selectedOptionId)}
						onChange={onOptionChange}
					/>
				</OptionSelectorInputStyled>
				{getEditInput()}
			</div>
		</div>
	);
});

export const OptionSelectorInputStyled = styled.div`
	${VerticalFlexStyle}
	margin-bottom: ${baseDistance.sm};
	align-items: flex-start;

	> div {
		border-color: transparent;

		> div.input {
			flex: none;
			width: auto;
		}
	}
`;
